import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useTour } from '../components/tour';
import { useTier } from '../context/TierContext';
import QuickStartChecklist from '../components/QuickStartChecklist';
import { getAssetIcon } from '../utils/industryIcons';
import { 
  Users, 
  Building2, 
  Briefcase, 
  Calendar,
  ArrowRight,
  Clock,
  MapPin,
  AlertTriangle,
  Shield,
  AlertCircle,
  ChevronRight,
  Bell,
  BookOpen,
  PlayCircle,
  GraduationCap,
  FileText,
  Eye,
  CalendarClock,
  CheckCircle2
} from 'lucide-react';
import { format } from 'date-fns';

const Dashboard = () => {
  const { api, user } = useAuth();
  const { hasFeature } = useTier();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [todaysJobs, setTodaysJobs] = useState([]);
  const [complianceAlerts, setComplianceAlerts] = useState(null);
  const [quoteAlerts, setQuoteAlerts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quickActionDialog, setQuickActionDialog] = useState(null); // 'job', 'equipment', 'operator', 'customer'

  const isOperator = user?.role === 'operator';
  const hasQuotes = hasFeature('quotes');
  
  // Get industry-specific asset icon
  const AssetIcon = getAssetIcon(user?.industry);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const promises = [
          api.get('/dashboard/stats'),
          isOperator ? api.get('/my-jobs', { params: { date: format(new Date(), 'yyyy-MM-dd') } }) : Promise.resolve({ data: [] }),
          !isOperator ? api.get('/compliance/alerts') : Promise.resolve({ data: null })
        ];
        
        // Fetch quote alerts for Advanced/Professional tiers
        if (!isOperator && hasQuotes) {
          promises.push(api.get('/quotes/alerts'));
        }
        
        const results = await Promise.all(promises);
        setStats(results[0].data);
        setTodaysJobs(isOperator ? results[1].data : []);
        setComplianceAlerts(results[2].data);
        
        if (!isOperator && hasQuotes && results[3]) {
          setQuoteAlerts(results[3].data);
        }
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [api, isOperator, hasQuotes]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-muted animate-pulse rounded" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 bg-muted animate-pulse rounded" />
          ))}
        </div>
      </div>
    );
  }

  // Operator Dashboard
  if (isOperator) {
    return (
      <div className="space-y-6" data-testid="operator-dashboard">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold">Welcome, {user?.full_name}</h1>
          <p className="text-muted-foreground">Today is {format(new Date(), 'EEEE, MMMM d, yyyy')}</p>
        </div>

        <Card className="border-[#ffd108] border-2">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-[#ffd108]" />
              Today's Jobs
            </CardTitle>
          </CardHeader>
          <CardContent>
            {todaysJobs.length === 0 ? (
              <p className="text-muted-foreground py-4 text-center">No jobs scheduled for today</p>
            ) : (
              <div className="space-y-3">
                {todaysJobs.map((item) => (
                  <div 
                    key={item.allocation.id} 
                    className="p-4 bg-muted rounded-sm border-l-4 border-[#ffd108]"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold">{item.customer?.company_name || 'Unknown Customer'}</h3>
                        <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                          <MapPin className="h-3 w-3" />
                          {item.site?.address || 'No address'}
                        </p>
                      </div>
                      <Badge variant={item.timesheet?.status === 'completed' ? 'default' : 'secondary'}>
                        {item.timesheet?.status || 'Pending'}
                      </Badge>
                    </div>
                    <div className="mt-3 flex items-center gap-4 text-sm">
                      <span className="flex items-center gap-1">
                        <AssetIcon className="h-4 w-4" />
                        {item.equipment?.equipment_type || item.equipment?.name || 'No equipment'} {item.equipment?.asset_id ? `(${item.equipment.asset_id})` : ''}
                      </span>
                      <span className="flex items-center gap-1 font-mono text-xs">
                        <Clock className="h-4 w-4" />
                        {item.allocation.start_date}
                      </span>
                    </div>
                    {item.job?.notes && (
                      <p className="mt-2 text-sm text-muted-foreground bg-background p-2 rounded">
                        {item.job.notes}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
            <Link to="/my-jobs">
              <Button className="w-full mt-4 btn-volt" data-testid="view-all-jobs-btn">
                View All My Jobs
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Dispatcher/Admin Dashboard
  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold">Dashboard</h1>
          <p className="text-muted-foreground">{format(new Date(), 'EEEE, MMMM d, yyyy')}</p>
        </div>
        <Link to="/schedule">
          <Button className="btn-volt" data-testid="open-schedule-btn">
            <Calendar className="h-4 w-4 mr-2" />
            Open Schedule
          </Button>
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Equipment Card - Clickable */}
        <Card 
          className="card-industrial cursor-pointer hover:border-[#ffd108] transition-all"
          onClick={() => navigate('/equipment')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="label-overline">Assets</p>
                <p className="text-3xl font-heading font-bold">{stats?.equipment?.total || 0}</p>
              </div>
              <div className="p-3 bg-[#ffd108]/20 rounded-sm">
                <AssetIcon className="h-6 w-6 text-[#ffd108]" />
              </div>
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              <span className="text-[#ffd108] font-medium">{stats?.equipment?.available || 0}</span> available
            </p>
          </CardContent>
        </Card>

        {/* Operators Card - Clickable */}
        <Card 
          className="card-industrial cursor-pointer hover:border-blue-500 transition-all"
          onClick={() => navigate('/operators')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="label-overline">Operators</p>
                <p className="text-3xl font-heading font-bold">{stats?.operators?.total || 0}</p>
              </div>
              <div className="p-3 bg-blue-500/20 rounded-sm">
                <Users className="h-6 w-6 text-blue-400" />
              </div>
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              <span className="text-blue-400 font-medium">{stats?.operators?.available || 0}</span> available
            </p>
          </CardContent>
        </Card>

        {/* Customers Card - Clickable */}
        <Card 
          className="card-industrial cursor-pointer hover:border-purple-500 transition-all"
          onClick={() => navigate('/customers')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="label-overline">Customers</p>
                <p className="text-3xl font-heading font-bold">{stats?.customers?.total || 0}</p>
              </div>
              <div className="p-3 bg-purple-500/20 rounded-sm">
                <Building2 className="h-6 w-6 text-purple-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Today's Jobs Card - Clickable */}
        <Card 
          className="card-industrial cursor-pointer hover:border-[#FF4F00] transition-all"
          onClick={() => {
            // Navigate to schedule with today filter
            navigate('/schedule', { state: { filterDate: 'today' } });
          }}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="label-overline">Today's Jobs</p>
                <p className="text-3xl font-heading font-bold">{stats?.jobs?.today || 0}</p>
              </div>
              <div className="p-3 bg-[#FF4F00]/20 rounded-sm">
                <Briefcase className="h-6 w-6 text-[#FF4F00]" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Job Status Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="card-industrial">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Jobs Overview (This Week)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Not Allocated</span>
                <div className="flex items-center gap-2">
                  <div className="h-2 w-24 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-[#EF4444] dark:bg-gray-500" 
                      style={{ width: `${stats?.jobs?.not_allocated ? (stats.jobs.not_allocated / (stats.jobs.not_allocated + stats.jobs.allocated + stats.jobs.confirmed + stats.jobs.docketed + stats.jobs.invoiced) * 100) : 0}%` }}
                    />
                  </div>
                  <span className="font-mono text-sm w-8 text-right">{stats?.jobs?.not_allocated || 0}</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Allocated</span>
                <div className="flex items-center gap-2">
                  <div className="h-2 w-24 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-[#F59E0B] dark:bg-amber-500" 
                      style={{ width: `${stats?.jobs?.allocated ? (stats.jobs.allocated / (stats.jobs.not_allocated + stats.jobs.allocated + stats.jobs.confirmed + stats.jobs.docketed + stats.jobs.invoiced) * 100) : 0}%` }}
                    />
                  </div>
                  <span className="font-mono text-sm w-8 text-right">{stats?.jobs?.allocated || 0}</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Confirmed</span>
                <div className="flex items-center gap-2">
                  <div className="h-2 w-24 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-[#22C55E] dark:bg-blue-500" 
                      style={{ width: `${stats?.jobs?.confirmed ? (stats.jobs.confirmed / (stats.jobs.not_allocated + stats.jobs.allocated + stats.jobs.confirmed + stats.jobs.docketed + stats.jobs.invoiced) * 100) : 0}%` }}
                    />
                  </div>
                  <span className="font-mono text-sm w-8 text-right">{stats?.jobs?.confirmed || 0}</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Docketed</span>
                <div className="flex items-center gap-2">
                  <div className="h-2 w-24 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-[#7C3AED] dark:bg-purple-500" 
                      style={{ width: `${stats?.jobs?.docketed ? (stats.jobs.docketed / (stats.jobs.not_allocated + stats.jobs.allocated + stats.jobs.confirmed + stats.jobs.docketed + stats.jobs.invoiced) * 100) : 0}%` }}
                    />
                  </div>
                  <span className="font-mono text-sm w-8 text-right">{stats?.jobs?.docketed || 0}</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Invoiced</span>
                <div className="flex items-center gap-2">
                  <div className="h-2 w-24 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-[#10B981] dark:bg-green-500" 
                      style={{ width: `${stats?.jobs?.invoiced ? (stats.jobs.invoiced / (stats.jobs.not_allocated + stats.jobs.allocated + stats.jobs.confirmed + stats.jobs.docketed + stats.jobs.invoiced) * 100) : 0}%` }}
                    />
                  </div>
                  <span className="font-mono text-sm w-8 text-right">{stats?.jobs?.invoiced || 0}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-industrial">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              <Button 
                variant="outline" 
                className="w-full justify-start" 
                data-testid="quick-action-jobs"
                onClick={() => navigate('/schedule', { state: { openJobDialog: true } })}
              >
                <Briefcase className="h-4 w-4 mr-2" />
                New Job
              </Button>
              {hasQuotes && (
                <Button 
                  variant="outline" 
                  className="w-full justify-start" 
                  data-testid="quick-action-quote"
                  onClick={() => navigate('/quotes/new')}
                >
                  <FileText className="h-4 w-4 mr-2" />
                  New Quote
                </Button>
              )}
              <Button 
                variant="outline" 
                className="w-full justify-start" 
                data-testid="quick-action-equipment"
                onClick={() => navigate('/equipment', { state: { openDialog: true } })}
              >
                <AssetIcon className="h-4 w-4 mr-2" />
                Add Asset
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start" 
                data-testid="quick-action-operators"
                onClick={() => navigate('/operators', { state: { openDialog: true } })}
              >
                <Users className="h-4 w-4 mr-2" />
                Add Operator
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start" 
                data-testid="quick-action-customers"
                onClick={() => navigate('/customers', { state: { openDialog: true } })}
              >
                <Building2 className="h-4 w-4 mr-2" />
                Add Customer
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Today's Allocations */}
      {stats?.todays_allocations?.length > 0 && (
        <Card className="card-industrial">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Today's Allocations</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 label-overline">Customer</th>
                    <th className="text-left py-2 label-overline">Asset</th>
                    <th className="text-left py-2 label-overline">Operator</th>
                    <th className="text-left py-2 label-overline">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.todays_allocations.slice(0, 5).map((alloc) => (
                    <tr key={alloc.id} className="border-b border-muted hover:bg-muted/50 cursor-pointer" onClick={() => navigate('/schedule')}>
                      <td className="py-2">
                        {alloc.customer_name || 'Unknown Customer'}
                      </td>
                      <td className="py-2">
                        <div className="flex items-center gap-1">
                          <AssetIcon className="h-3 w-3 text-[#ffd108]" />
                          {alloc.equipment_name || 'N/A'}
                        </div>
                      </td>
                      <td className="py-2">
                        <div className="flex items-center gap-1">
                          <Users className="h-3 w-3 text-blue-500" />
                          {alloc.operator_name || 'N/A'}
                        </div>
                      </td>
                      <td className="py-2 font-mono text-xs">{alloc.start_date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {stats.todays_allocations.length > 5 && (
              <Button 
                variant="outline" 
                className="w-full mt-4"
                onClick={() => navigate('/schedule', { state: { filterDate: 'today' } })}
              >
                View All {stats.todays_allocations.length} Allocations
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Compliance Alerts Widget */}
      {complianceAlerts && complianceAlerts.summary?.total_alerts > 0 && (
        <Card className="card-industrial border-red-500/30">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Shield className="h-5 w-5 text-red-500" />
                Compliance Alerts
                <Badge className="bg-red-500/20 text-red-400 border border-red-500/30 ml-2">
                  {complianceAlerts.summary.total_alerts}
                </Badge>
              </CardTitle>
              <Link to="/compliance">
                <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300">
                  View All
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Summary Badges */}
              <div className="flex flex-wrap gap-2">
                {complianceAlerts.summary.overdue_count > 0 && (
                  <Badge className="bg-red-500/20 text-red-400 border border-red-500/30">
                    <AlertCircle className="h-3 w-3 mr-1" />
                    {complianceAlerts.summary.overdue_count} Overdue
                  </Badge>
                )}
                {complianceAlerts.summary.critical_count > 0 && (
                  <Badge className="bg-red-500/20 text-red-400 border border-red-500/30">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    {complianceAlerts.summary.critical_count} Expiring in 7 days
                  </Badge>
                )}
                {complianceAlerts.summary.warning_count > 0 && (
                  <Badge className="bg-red-500/20 text-red-400 border border-red-500/30">
                    <Bell className="h-3 w-3 mr-1" />
                    {complianceAlerts.summary.warning_count} Expiring in 14 days
                  </Badge>
                )}
                {complianceAlerts.summary.upcoming_count > 0 && (
                  <Badge className="bg-red-500/20 text-red-400 border border-red-500/30">
                    {complianceAlerts.summary.upcoming_count} Expiring in 30 days
                  </Badge>
                )}
              </div>

              {/* Alert Items - Show most urgent first */}
              <div className="space-y-2">
                {/* Overdue Items */}
                {complianceAlerts.overdue?.slice(0, 3).map((alert) => (
                  <div key={alert.id} className="flex items-center gap-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <AlertCircle className="h-5 w-5 text-red-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-red-300 truncate">
                        {alert.entity_name} - {alert.type_name}
                      </p>
                      <p className="text-xs text-red-400/80">
                        Expired {Math.abs(alert.days_until_expiry)} days ago
                      </p>
                    </div>
                    <Badge className="bg-red-500 text-white shrink-0">Overdue</Badge>
                  </div>
                ))}

                {/* Critical Items (7 days) */}
                {complianceAlerts.critical?.slice(0, 3 - (complianceAlerts.overdue?.length || 0)).map((alert) => (
                  <div key={alert.id} className="flex items-center gap-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <AlertTriangle className="h-5 w-5 text-red-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-red-300 truncate">
                        {alert.entity_name} - {alert.type_name}
                      </p>
                      <p className="text-xs text-red-400/80">
                        Expires in {alert.days_until_expiry} day{alert.days_until_expiry !== 1 ? 's' : ''}
                      </p>
                    </div>
                    <Badge className="bg-red-500 text-white shrink-0">Critical</Badge>
                  </div>
                ))}

                {/* Warning Items (14 days) - only if space */}
                {complianceAlerts.warning?.slice(0, Math.max(0, 3 - (complianceAlerts.overdue?.length || 0) - (complianceAlerts.critical?.length || 0))).map((alert) => (
                  <div key={alert.id} className="flex items-center gap-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <Bell className="h-5 w-5 text-red-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-red-300 truncate">
                        {alert.entity_name} - {alert.type_name}
                      </p>
                      <p className="text-xs text-red-400/80">
                        Expires in {alert.days_until_expiry} days
                      </p>
                    </div>
                    <Badge className="bg-red-500 text-white shrink-0">Warning</Badge>
                  </div>
                ))}
              </div>

              {complianceAlerts.summary.total_alerts > 3 && (
                <Link to="/compliance" className="block">
                  <Button variant="outline" className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10">
                    View All {complianceAlerts.summary.total_alerts} Alerts
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* No Alerts - Good Status */}
      {complianceAlerts && complianceAlerts.summary?.total_alerts === 0 && (
        <Card className="card-industrial border-green-500/30 bg-gradient-to-br from-green-500/5 to-transparent">
          <CardContent className="py-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-500/20 rounded-full">
                <Shield className="h-6 w-6 text-green-500" />
              </div>
              <div>
                <p className="font-heading font-semibold text-green-500">All Compliance Up to Date</p>
                <p className="text-sm text-muted-foreground">No expiring certifications or documents in the next 30 days.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quotes Requiring Action Widget - Advanced/Professional only */}
      {hasQuotes && quoteAlerts && quoteAlerts.total_alerts > 0 && (
        <Card className="card-industrial border-blue-500/30 bg-gradient-to-br from-blue-500/5 to-transparent" data-testid="quotes-action-widget">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-500" />
                Quotes Requiring Action
                <Badge className="bg-blue-500/20 text-blue-400 border border-blue-500/30 ml-2">
                  {quoteAlerts.total_alerts}
                </Badge>
              </CardTitle>
              <Link to="/quotes">
                <Button variant="ghost" size="sm" className="text-blue-400 hover:text-blue-300">
                  View All
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Summary Badges */}
              <div className="flex flex-wrap gap-2">
                {quoteAlerts.expiring_soon?.length > 0 && (
                  <Badge className="bg-blue-500/20 text-blue-400 border border-blue-500/30">
                    <CalendarClock className="h-3 w-3 mr-1" />
                    {quoteAlerts.expiring_soon.length} Expiring Soon
                  </Badge>
                )}
                {quoteAlerts.awaiting_response?.length > 0 && (
                  <Badge className="bg-blue-500/20 text-blue-400 border border-blue-500/30">
                    <Eye className="h-3 w-3 mr-1" />
                    {quoteAlerts.awaiting_response.length} Viewed, No Response
                  </Badge>
                )}
                {quoteAlerts.pending_conversion?.length > 0 && (
                  <Badge className="bg-blue-500/20 text-blue-400 border border-blue-500/30">
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    {quoteAlerts.pending_conversion.length} Approved, Not Converted
                  </Badge>
                )}
              </div>

              {/* Alert Items */}
              <div className="space-y-2">
                {/* Expiring Soon */}
                {quoteAlerts.expiring_soon?.slice(0, 2).map((quote) => (
                  <div 
                    key={quote.id} 
                    className="flex items-center gap-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg cursor-pointer hover:bg-blue-500/20 transition-colors"
                    onClick={() => navigate(`/quotes/${quote.id}`)}
                  >
                    <CalendarClock className="h-5 w-5 text-blue-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-blue-300 truncate">
                        {quote.quote_number} - {quote.customer_name}
                      </p>
                      <p className="text-xs text-blue-400/80">
                        Expires in {quote.days_until_expiry} day{quote.days_until_expiry !== 1 ? 's' : ''} • ${quote.total?.toLocaleString()}
                      </p>
                    </div>
                    <Badge className="bg-blue-500 text-white shrink-0">Expiring</Badge>
                  </div>
                ))}

                {/* Awaiting Response (Viewed but no action) */}
                {quoteAlerts.awaiting_response?.slice(0, 2 - (quoteAlerts.expiring_soon?.slice(0, 2).length || 0)).map((quote) => (
                  <div 
                    key={quote.id} 
                    className="flex items-center gap-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg cursor-pointer hover:bg-blue-500/20 transition-colors"
                    onClick={() => navigate(`/quotes/${quote.id}`)}
                  >
                    <Eye className="h-5 w-5 text-blue-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-blue-300 truncate">
                        {quote.quote_number} - {quote.customer_name}
                      </p>
                      <p className="text-xs text-blue-400/80">
                        Viewed {quote.days_since_viewed} day{quote.days_since_viewed !== 1 ? 's' : ''} ago • ${quote.total?.toLocaleString()}
                      </p>
                    </div>
                    <Badge className="bg-blue-500 text-white shrink-0">Follow Up</Badge>
                  </div>
                ))}

                {/* Pending Conversion (Approved but not converted to job) */}
                {quoteAlerts.pending_conversion?.slice(0, Math.max(0, 3 - (quoteAlerts.expiring_soon?.slice(0, 2).length || 0) - (quoteAlerts.awaiting_response?.slice(0, 2).length || 0))).map((quote) => (
                  <div 
                    key={quote.id} 
                    className="flex items-center gap-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg cursor-pointer hover:bg-blue-500/20 transition-colors"
                    onClick={() => navigate(`/quotes/${quote.id}`)}
                  >
                    <CheckCircle2 className="h-5 w-5 text-blue-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-blue-300 truncate">
                        {quote.quote_number} - {quote.customer_name}
                      </p>
                      <p className="text-xs text-blue-400/80">
                        Approved {quote.days_since_approved} day{quote.days_since_approved !== 1 ? 's' : ''} ago • ${quote.total?.toLocaleString()}
                      </p>
                    </div>
                    <Badge className="bg-blue-500 text-white shrink-0">Convert to Job</Badge>
                  </div>
                ))}
              </div>

              {quoteAlerts.total_alerts > 3 && (
                <Link to="/quotes" className="block">
                  <Button variant="outline" className="w-full border-blue-500/30 text-blue-400 hover:bg-blue-500/10">
                    View All {quoteAlerts.total_alerts} Quote Alerts
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Start Checklist */}
      <QuickStartChecklist />

      {/* Help Centre & Training Card */}
      <HelpCentreCard />
    </div>
  );
};

// Help Centre Card Component
const HelpCentreCard = () => {
  const navigate = useNavigate();
  const { startTour, hasCompletedTour, hasSkippedTour } = useTour();
  const showTourOption = hasCompletedTour || hasSkippedTour;

  return (
    <Card className="card-industrial border-[#ffd108]/30 bg-gradient-to-br from-[#ffd108]/5 to-transparent">
      <CardContent className="p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-[#ffd108]/20 rounded-xl">
            <GraduationCap className="h-6 w-6 text-[#ffd108]" />
          </div>
          <div className="flex-1">
            <h3 className="font-heading font-semibold text-lg mb-1">Help Centre</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Training guides, tutorials, and support resources to help you get the most out of EQUIPR.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button 
                onClick={() => navigate('/help')}
                className="btn-volt"
                data-testid="dashboard-help-centre-btn"
              >
                <BookOpen className="h-4 w-4 mr-2" />
                Browse Guides
              </Button>
              {showTourOption && (
                <Button 
                  variant="outline"
                  onClick={startTour}
                  data-testid="dashboard-restart-tour-btn"
                >
                  <PlayCircle className="h-4 w-4 mr-2" />
                  Restart Tour
                </Button>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default Dashboard;
